import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Ticket, Loader2 } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleGoogleCallback } = useAuth();
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      console.log('[AuthCallback] Starting callback processing');
      console.log('[AuthCallback] Full URL:', window.location.href);
      console.log('[AuthCallback] Hash:', window.location.hash);
      console.log('[AuthCallback] Search:', window.location.search);
      
      // Get session_id from URL hash fragment (format: #session_id=xxx)
      const hash = window.location.hash;
      let sessionId = null;
      
      if (hash && hash.includes('session_id=')) {
        const params = new URLSearchParams(hash.substring(1)); // Remove the # prefix
        sessionId = params.get('session_id');
        console.log('[AuthCallback] Found session_id in hash:', sessionId?.substring(0, 20) + '...');
      }
      
      // Also check query params as fallback
      const searchParams = new URLSearchParams(location.search);
      if (!sessionId) {
        sessionId = searchParams.get('session_id');
        if (sessionId) {
          console.log('[AuthCallback] Found session_id in query:', sessionId?.substring(0, 20) + '...');
        }
      }
      
      const errorParam = searchParams.get('error');

      if (errorParam) {
        console.error('[AuthCallback] Error from OAuth:', errorParam);
        setError(errorParam);
        toast.error(`Authentication failed: ${errorParam}`);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (!sessionId) {
        console.error('[AuthCallback] No session_id found in URL');
        setError('No session ID received');
        toast.error('Authentication failed: No session ID');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        console.log('[AuthCallback] Calling handleGoogleCallback...');
        const user = await handleGoogleCallback(sessionId);
        console.log('[AuthCallback] Success! User:', user?.email);
        toast.success(`Welcome, ${user.display_name}!`);
        
        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);
        
        if (user.role === 'influencer') {
          navigate('/dashboard', { replace: true });
        } else if (user.role === 'admin') {
          navigate('/admin', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } catch (err) {
        console.error('[AuthCallback] Error:', err);
        console.error('[AuthCallback] Error message:', err.message);
        const errorMessage = err.message || 'Authentication failed';
        setError(errorMessage);
        toast.error(errorMessage);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processCallback();
  }, [handleGoogleCallback, navigate, location.search]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 rounded-xl bg-primary/20 flex items-center justify-center mx-auto mb-6">
          <Ticket className="w-8 h-8 text-primary" />
        </div>
        
        {error ? (
          <>
            <h2 className="font-heading font-bold text-2xl mb-2 text-red-400">Authentication Failed</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <p className="text-sm text-muted-foreground">Redirecting to login...</p>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl mb-2">Signing you in...</h2>
            <p className="text-muted-foreground">Please wait while we complete your authentication.</p>
          </>
        )}
      </div>
    </div>
  );
}
